<template>
  <form
    data-test-id="login-form"
    @submit.prevent="login">
    <FormInput
      id="email"
      v-model="email"
      type="email"
      name="email"
      data-test-id="email"
      autocomplete="username"
      :validation="v$.email"
      required
      validate
      :label="$t('general/emailaddress')" />

    <FormPassword
      id="password"
      v-model="password"
      data-test-id="password"
      class="h-margin-top"
      name="password"
      type="password"
      autocomplete="current-password"
      :validation="v$.password"
      :label="$t('auth/password')" />

    <LazyNotification
      v-for="(error, key) in errors"
      :key="key"
      data-test-id="error-message"
      class="h-margin-top">
      {{ error.message }}
      <AuthRateLimiter v-if="error.type === 'ThrottleException' && limited" :seconds-left="secondsLeft" @ended="reload" />
    </LazyNotification>

    <Btn
      class="h-margin-top"
      shape="pill"
      color="cta"
      full-width="all"
      type="submit"
      :loading="loading"
      :disabled="limited"
      data-test-id="submit">
      {{ checkoutLogin ? $t('auth/checkout-login') : $t('auth/login') }}
    </Btn>

    <Btn
      v-if="!checkoutLogin || errors"
      full-width="all"
      color="link"
      :to="{ name: $lang + '-forgot-password' }">
      {{ $t('auth/forgot-password') }}
    </Btn>
  </form>
</template>
<script setup>
import { useVuelidate } from '@vuelidate/core'
import { email as emailValidator, required, minLength } from '@vuelidate/validators'
const { $lang } = useNuxtApp()
const authStore = useAuthStore()
const { limited, secondsLeft } = useRateLimiter()
const route = useRoute()
const router = useRouter()
const email = ref('')
const password = ref('')
const errors = ref(null)
const loading = ref(false)
const { showToast } = useToast()
await useI18n(['auth', 'account'])
const rules = {
  email: {
    required,
    emailValidator
  },
  password: {
    minLength: minLength(8),
    required
  }
}

defineProps({
  checkoutLogin: {
    type: Boolean,
    default: false
  }
})
const cartStore = useCartStore()
const v$ = useVuelidate(rules, { email, password })
const redirectTo = async () => {
  await nextTick()
  if (route.query.redirect) {
    await router.push({
      path: route.query.redirect
    })
    return true
  }
  await router.push({ name: `${$lang}-index` })
}

const reload = () => {
  window.location.reload()
}

const login = async () => {
  v$.value.$validate()
  if (v$.value.$invalid) { return }
  loading.value = true

  try {
    const data = await authStore.login({
      email: email.value,
      password: password.value
    })

    if (data) {
      showToast({
        message: $t('account/log-in'),
        impact: 'success',
        icon: 'System/check-line'
      })

      await cartStore.sync(true)
      if (cartStore.showMergeList) {
        await router.push({ name: `${$lang}-cart` })
      } else {
        redirectTo()
      }
    }
  } catch (e) {
    errors.value = e.response._data?.errors
    password.value = null
    v$.value.password.$reset()
  }

  loading.value = false
}

</script>
